import type { Entity, EntityMap } from '@ms/yammer-data-support';

import type { YammerState } from '../state/types';

type EntityMapBucketKeys = { [K in keyof YammerState]: [K, YammerState[K]] }[keyof YammerState] extends infer T
  ? T extends [infer K, EntityMap<Entity>]
    ? K
    : never
  : never;

type BucketEntity<B extends EntityMapBucketKeys> = YammerState[B] extends EntityMap<infer E> ? E : never;

type GetEntities = <B extends EntityMapBucketKeys>(
  state: Pick<YammerState, B>,
  bucket: B
) => Record<string, BucketEntity<B>>;
const getEntities: GetEntities = <B extends EntityMapBucketKeys>(state: Pick<YammerState, B>, bucket: B) => {
  const entities = state[bucket] as EntityMap<BucketEntity<B>>;

  if (!entities) {
    throw new Error(`Can not get entities in '${bucket}'`);
  }

  return entities.fulfilled.byId;
};

export type FindById<B extends EntityMapBucketKeys> = (
  state: Pick<YammerState, B>,
  id: BucketEntity<B>['id']
) => BucketEntity<B> | undefined;
type CreateFindById = <B extends EntityMapBucketKeys>(bucket: B) => FindById<B>;
export const createFindById: CreateFindById = (bucket) => (state, id) => {
  const entity = getEntities(state, bucket)[id];

  if (!entity) {
    return;
  }

  return entity;
};

export type FindByIdThrows<B extends EntityMapBucketKeys> = (
  state: Pick<YammerState, B>,
  id: BucketEntity<B>['id']
) => BucketEntity<B>;
type CreateFindByIdThrows = <B extends EntityMapBucketKeys>(bucket: B) => FindByIdThrows<B>;
export const createFindByIdThrows: CreateFindByIdThrows = (bucket) => (state, id) => {
  const entity = getEntities(state, bucket)[id];

  if (!entity) {
    throw new Error(`Can not find entity '${id}' in bucket [${bucket}]`);
  }

  return entity;
};
